import React, { useState } from 'react';
import { Brain, Plus, CheckCircle, Clock, AlertTriangle, Sparkles, TrendingUp } from 'lucide-react';

export const AutismABAModule: React.FC = () => {
  const [abcLogs, setAbcLogs] = useState([
    { id: '1', time: '10:15 AM', antecedent: 'Transition from recess to indoor reading', behavior: 'Verbal vocalization / protest', consequence: 'Offered sensory break with weighted lap pad', result: 'Calmed in 3 mins' },
    { id: '2', time: '11:45 AM', antecedent: 'Loud bell sound in hallway', behavior: 'Covered ears & sought quiet corner', consequence: 'Provided noise-canceling headphones', result: 'Resumed task' },
  ]);

  const [antecedent, setAntecedent] = useState('');
  const [behavior, setBehavior] = useState('');
  const [consequence, setConsequence] = useState('');

  const handleAddAbcLog = (e: React.FormEvent) => {
    e.preventDefault();
    if (!antecedent || !behavior) return;
    setAbcLogs([
      ...abcLogs,
      {
        id: Date.now().toString(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        antecedent,
        behavior,
        consequence: consequence || 'Redirection offered',
        result: 'Logged successfully'
      }
    ]);
    setAntecedent('');
    setBehavior('');
    setConsequence('');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <span className="px-2.5 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-full uppercase tracking-wider">
            CentralReach & BehaviorTracker Benchmark
          </span>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">Autism & ABA Behavior Analysis</h2>
          <p className="text-xs text-slate-500">Antecedent-Behavior-Consequence (ABC) logger & sensory reinforcement tracker</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* ABC Log Entry Form */}
        <form onSubmit={handleAddAbcLog} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
            <Plus className="w-4 h-4 text-purple-600" />
            Log ABC Incident
          </h3>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">Antecedent (Trigger)</label>
            <input 
              type="text" 
              value={antecedent} 
              onChange={(e) => setAntecedent(e.target.value)}
              placeholder="e.g. Demand placed for math worksheet"
              className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">Target Behavior</label>
            <input 
              type="text" 
              value={behavior} 
              onChange={(e) => setBehavior(e.target.value)}
              placeholder="e.g. Elopement from desk"
              className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">Consequence / Strategy Used</label>
            <input 
              type="text" 
              value={consequence} 
              onChange={(e) => setConsequence(e.target.value)}
              placeholder="e.g. First-Then visual token board"
              className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>

          <button
            type="submit"
            className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded text-xs transition"
          >
            Save ABC Entry
          </button>
        </form>

        {/* ABC Log Roster Stream */}
        <div className="md:col-span-2 space-y-3">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Today's ABA Trial Log</h3>
          <div className="space-y-2.5">
            {abcLogs.map((log) => (
              <div key={log.id} className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-sm text-xs space-y-1.5">
                <div className="flex items-center justify-between text-slate-500 font-bold">
                  <span className="flex items-center gap-1 text-purple-700">
                    <Clock className="w-3.5 h-3.5" />
                    {log.time}
                  </span>
                  <span className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-bold">
                    {log.result}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1 text-[11px]">
                  <div><strong className="text-slate-700 block">Antecedent:</strong> {log.antecedent}</div>
                  <div><strong className="text-slate-700 block">Behavior:</strong> {log.behavior}</div>
                  <div><strong className="text-slate-700 block">Consequence:</strong> {log.consequence}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
